import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { EngineTrackResponseExpanded } from '@engine/models/tracking'

import { adminFetch } from '#api/client.js'
import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { CopyButton } from '#components/CopyButton.js'
import { CopyRequestButton } from '#components/CopyRequestButton.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { TrackingDetails } from './TrackingDetails.jsx'
import { TrackingsList } from './TrackingsList.jsx'

const TrackingsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedTracking, setSelectedTracking] =
    useState<EngineTrackResponseExpanded | null>(null)

  const [loadNum, setLoadNum] = useState(searchParams.get('loadNum') || '')
  const [proNum, setProNum] = useState(searchParams.get('proNum') || '')
  const [trackingId, setTrackingId] = useState(
    searchParams.get('trackingId') || '',
  )
  const [status, setStatus] = useState(searchParams.get('status') || '')
  const [pickupDateFrom, setPickupDateFrom] = useState(
    searchParams.get('pickupDateFrom') || '',
  )
  const [pickupDateTo, setPickupDateTo] = useState(
    searchParams.get('pickupDateTo') || '',
  )
  const [createdAtFrom, setCreatedAtFrom] = useState(
    searchParams.get('createdAtFrom') || '',
  )
  const [createdAtTo, setCreatedAtTo] = useState(
    searchParams.get('createdAtTo') || '',
  )
  const [sortBy, setSortBy] = useState(
    searchParams.get('sortBy') || '-createdAt',
  )
  const [scac, setScac] = useState(searchParams.get('scac') || '')

  const {
    items: trackings,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<EngineTrackResponseExpanded>('trackings', {
    loadNum,
    proNum,
    trackingId,
    status,
    pickupDateFrom,
    pickupDateTo,
    createdAtFrom,
    createdAtTo,
    scac,
    sort: sortBy,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (loadNum) params.set('loadNum', loadNum)
    if (proNum) params.set('proNum', proNum)
    if (trackingId) params.set('trackingId', trackingId)
    if (status) params.set('status', status)
    if (pickupDateFrom) params.set('pickupDateFrom', pickupDateFrom)
    if (pickupDateTo) params.set('pickupDateTo', pickupDateTo)
    if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
    if (createdAtTo) params.set('createdAtTo', createdAtTo)
    if (sortBy !== '-createdAt') params.set('sortBy', sortBy)
    if (scac) params.set('scac', scac)
    setSearchParams(params, { replace: true })
  }, [
    loadNum,
    proNum,
    trackingId,
    status,
    pickupDateFrom,
    pickupDateTo,
    createdAtFrom,
    createdAtTo,
    sortBy,
    scac,
    setSearchParams,
  ])

  const handleTrackingSelect = useCallback(
    (tracking: EngineTrackResponseExpanded) => {
      setSelectedTracking(tracking)
    },
    [],
  )

  const handleCloseDetails = useCallback(() => {
    setSelectedTracking(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Tracking Details</span>
      {selectedTracking && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?trackingId=${selectedTracking._id}`}
          />
          <CopyRequestButton
            label="Copy Tracking Request"
            getContent={() =>
              adminFetch(`trackings/${selectedTracking._id}/service-request`)
            }
          />
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Tracking Subscriptions"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No tracking subscriptions found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <FilterInput
                label="Load Number"
                value={loadNum}
                onChange={setLoadNum}
                placeholder="Search by load number..."
              />
              <FilterInput
                label="PRO Number"
                value={proNum}
                onChange={setProNum}
                placeholder="Search by PRO number..."
              />
              <FilterInput
                label="Tracking ID"
                value={trackingId}
                onChange={setTrackingId}
                placeholder="Search by tracking ID..."
              />
            </FilterRow>

            <FilterRow>
              <FilterSelect
                label="Status"
                value={status}
                onChange={setStatus}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'pending', label: 'Pending' },
                  {
                    value: 'enroute-to-pickup',
                    label: 'Enroute to Pickup',
                  },
                  { value: 'in-transit', label: 'In Transit' },
                  { value: 'out-for-delivery', label: 'Out for Delivery' },
                  { value: 'delivered', label: 'Delivered' },
                  { value: 'not-found', label: 'Not Found' },
                  { value: 'error', label: 'Error' },
                ]}
              />
              <ScacTypeahead value={scac} onChange={value => setScac(value)} />
              <FilterInput
                label="Pickup Date From"
                value={pickupDateFrom}
                onChange={setPickupDateFrom}
                type="date"
              />
              <FilterInput
                label="Pickup Date To"
                value={pickupDateTo}
                onChange={setPickupDateTo}
                type="date"
              />
            </FilterRow>

            <FilterRow>
              <FilterInput
                label="Created Date From"
                value={createdAtFrom}
                onChange={setCreatedAtFrom}
                type="date"
              />
              <FilterInput
                label="Created Date To"
                value={createdAtTo}
                onChange={setCreatedAtTo}
                type="date"
              />
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          trackings.length > 0 ? (
            <TrackingsList
              trackings={trackings}
              selectedId={selectedTracking?._id}
              onSelect={handleTrackingSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
              sortBy={sortBy}
              onSortChange={setSortBy}
              onUpdatesTriggered={refresh}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedTracking}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <TrackingDetails tracking={selectedTracking} />
      </Slider>
    </>
  )
}

export { TrackingsView }
